import React, {Component, Fragment} from "react"
import {connect} from "react-redux"
import {Row, Col} from "react-bootstrap"
import {Emoji} from "emoji-mart"
import {get} from "lodash"

import authentication from "@irati/auth"
import id from "@irati/utils/id"
import {injectIntl, FormattedMessage, InjectIntl} from "@irati/utils/lang"
import {H2} from "@irati/ui/src//Typography/Heading"
import {Para} from "@irati/ui/src/Labels"
import {AccordionItem} from "@irati/ui/src/Accordion"
import Link from "@irati/ui/src/Link"
import Modal from "@irati/ui/src/Modal"
import medicalTracking from "@irati-tracking/medical"
import {MedicalDCAUser} from "@irati/medical/types"
import {ReduxState} from "@irati/store/types"
import isMobileDevice from "@irati/utils/mobile"

import {Grid, TextMeInstructions, TellMeMore, ElevatedCard} from "../../../components/shared"
import ContinueOnline from "../../../components/ContinueOnline"
import Popup from "../../../components/Popup"
import MakeAClaim from "../../../components/MakeAClaim"
import LiveChat from "../../../components/LiveChat"
import HelpEmail from "../../../components/HelpEmail"
import Video from "../../../components/Video"
import {MY_MEDICAL} from "../../../constants/tracking"
import {
  Flex,
  InstructionsButton,
  InfoSections,
  EmailUsAtPara,
  Accordions,
  MarginLeft} from "./styled"

const MEDICAL_DEFAULT_PHONENUMBER = "01111111111"
const MEDICAL_DEFAULT_ACTIVATIONCODE = "activationCode"
const MEDICAL_DEFAULT_EMAIL = "medical-test-email@mailinator.com"

interface Props extends InjectIntl {
  user: MedicalDCAUser,
  isPhone: boolean
}

interface State {
  show: boolean,
  title: string|null
}

class MyMedical extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      show: false,
      title: null
    }
    this.bookConsultationAccordion = React.createRef()
    this.makeAClaimAccordion = React.createRef()
    this.membershipCertificateAccordion = React.createRef()
    medicalTracking.viewHome(props.user.activationCode)
  }

  showModal = (title: string) => {
    const {formatMessage} = this.props
    const trackingLocation = formatMessage({id: title})
    medicalTracking.viewMoreInfo(this.props.user.activationCode, trackingLocation)
    this.setState({show: true, title})
  }

  hideModal = () => {
    this.setState({show: false})
  }

  closeAccordions = (currentRef) => {
    const shouldOpen = !currentRef.current.state.isExpanded
    if (currentRef !== this.bookConsultationAccordion) {
      this.bookConsultationAccordion.current.close()
    }
    if (this.makeAClaimAccordion.current && currentRef !== this.makeAClaimAccordion) {
      this.makeAClaimAccordion.current.close()
    }
    if (currentRef !== this.membershipCertificateAccordion) {
      this.membershipCertificateAccordion.current.close()
    }
    if (shouldOpen) {
      currentRef.current.open()
    }
  }

  handleMembershipCertificateClick = () => {
    medicalTracking.downloadCertificate(this.props.user.activationCode)
  }

  handleMakeAClaimClick = () => {
    medicalTracking.makeClaim(this.props.user.activationCode)
  }

  handleContinueOnline = () => {
    medicalTracking.continueOnline(this.props.user.activationCode, MY_MEDICAL)
  }

  render() {
    const idNamespace = id("my-medical", this.props)
    const {show, title} = this.state
    const {formatMessage, isPhone, user: {
      activationCode = MEDICAL_DEFAULT_ACTIVATIONCODE,
      phoneNumber = MEDICAL_DEFAULT_PHONENUMBER,
      email = MEDICAL_DEFAULT_EMAIL}
    } = this.props
    const hasTier2Access = authentication.apps("medical.tiers.tier2")
    const hasTier3Access = authentication.apps("medical.tiers.tier3")

    return (
      <Grid>
        <Row>
          <Col xs={12} sm={6}>
            <H2 base>
              <FormattedMessage id="medical.myMedical.heading" />
            </H2>
            {isPhone && <Video />}
            <Para large>
              <FormattedMessage id="medical.myMedical.para" />
            </Para>
            <InfoSections>
              <TellMeMore onClick={this.showModal} emoji="female-doctor" title="medical.information.onlineGPconsultations.title" />
              <TellMeMore onClick={this.showModal} emoji="memo" title="medical.information.prescriptionService.title" />
              {hasTier2Access && (
                <Fragment>
                  <TellMeMore
                    onClick={this.showModal}
                    emoji="thermometer"
                    title="medical.information.diagnosticInvestigations.title" />
                  <TellMeMore onClick={this.showModal} emoji="muscle" title="medical.information.physiotherapy.title" />
                </Fragment>
              )}
              {hasTier3Access &&
                <Fragment>
                  <TellMeMore onClick={this.showModal} emoji="busts_in_silhouette" title="medical.information.specialists.title" />
                  <TellMeMore onClick={this.showModal} emoji="male-doctor" title="medical.information.hospitalCharges.title" />
                  <TellMeMore onClick={this.showModal} emoji="bed" title="medical.information.homeNursing.title" />
                  <TellMeMore onClick={this.showModal} emoji="pound" title="medical.information.NHScashBenefit.title" />
                  <TellMeMore
                    onClick={this.showModal}
                    emoji="speaking_head_in_silhouette"
                    title="medical.information.mentalHealthCounselling.title" />
                </Fragment>
              }
            </InfoSections>
            <ElevatedCard>
              <Para large bold><FormattedMessage id="medical.myMedical.help.title" /></Para>
              <Para><FormattedMessage id="medical.myMedical.help.para" /></Para>
              <Flex>
                <Emoji emoji={{id: "iphone"}} size={14} set="apple" />
                <LiveChat />
                <EmailUsAtPara>
                  <FormattedMessage id="medical.myMedical.help.emailUsAt" />
                </EmailUsAtPara>
              </Flex>
              <Flex>
                <Emoji emoji={{id: "e-mail"}} size={14} set="apple" />
                <HelpEmail />
              </Flex>
            </ElevatedCard>
          </Col>
          <Col xs={12} sm={6}>
            {!isPhone && <Video />}
            <Accordions>
              <div onClick={() => this.closeAccordions(this.bookConsultationAccordion)}>
                <AccordionItem
                  ref={this.bookConsultationAccordion}
                  id={id("accordionItemConsultation", idNamespace)}
                  title={formatMessage({id: "medical.myMedical.accordionItem.bookConsultation.title"})}>
                  {isMobileDevice() ?
                    <>
                      <Para large light>
                        <FormattedMessage id="medical.myMedical.openApp.text1" />
                        <MarginLeft>
                          <Emoji emoji={{id: "thumbsup"}} size={14} set="apple" />
                        </MarginLeft>
                      </Para>
                      <Para large light>
                        <FormattedMessage id="medical.myMedical.openApp.text2" />
                      </Para>
                      <Para large light>
                        <FormattedMessage id="medical.myMedical.openApp.text3" />
                      </Para>
                    </> :
                    <Para large light>
                      <FormattedMessage id="medical.myMedical.accordionItem.bookConsultation.para" />
                    </Para>
                  }
                  <InstructionsButton>
                    <TextMeInstructions
                      isFull
                      phoneNumber={phoneNumber}
                      activationCode={activationCode}
                      trackingLocation={MY_MEDICAL} />
                  </InstructionsButton>
                  <ContinueOnline
                    onClick={this.handleContinueOnline}
                    activationCode={activationCode}
                    link="medical.myMedical.accordionItem.bookConsultation.href"
                    text="medical.myMedical.accordionItem.bookConsultation.link" />
                </AccordionItem>
              </div>
              {hasTier2Access &&
                <div onClick={() => this.closeAccordions(this.makeAClaimAccordion)}>
                  <AccordionItem
                    ref={this.makeAClaimAccordion}
                    id={id("accordionItemMakeAClaim", idNamespace)}
                    title={formatMessage({id: "medical.myMedical.accordionItem.makeAClaim.title"})}>
                    <MakeAClaim onClick={this.handleMakeAClaimClick} />
                  </AccordionItem>
                </div>
              }
              <div onClick={() => this.closeAccordions(this.membershipCertificateAccordion)}>
                <AccordionItem
                  ref={this.membershipCertificateAccordion}
                  id={id("accordionItemMembershipCertificate", idNamespace)}
                  title={formatMessage({id: "medical.myMedical.accordionItem.membershipCertificate.title"})}>
                  <Para>
                    <FormattedMessage id="medical.myMedical.accordionItem.membershipCertificate.para" values={{email}} />
                  </Para>
                </AccordionItem>
              </div>
            </Accordions>
            <Para><FormattedMessage id="medical.myMedical.privacy.para" /></Para>
            <Para>
              <FormattedMessage id="medical.myMedical.privacy.link.start" />
              <Link
                inBody
                uppercase={false}
                href={formatMessage({id: "medical.privacyPolicy.href"})}
                target="_blank"
                rel="noopener noreferrer">
                <FormattedMessage id="platform.footer.href.privacyPolicy.text" />
              </Link>
              <FormattedMessage id="medical.myMedical.privacy.link.end" />
            </Para>
          </Col>
        </Row>
        <Modal show={show} onHide={this.hideModal}>
          <Popup title={title} activationCode={activationCode} />
        </Modal>
      </Grid>
    )
  }
}

const mapStateToProps = ({mq}: ReduxState) => ({
  isPhone: get(mq, "lessThan.medium", false)
})

export default injectIntl(connect(mapStateToProps)(MyMedical))
